import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { formatHours, formatSeconds } from "@/lib/format";
import { useTimer } from "@/contexts/TimerContext";
import type { TimeEntry } from "@/hooks/use-time-registration";
import type { InternalProjectTask } from "@/hooks/use-internal-project-tasks";
import { internalTaskToMyTask } from "@/hooks/use-internal-project-tasks";
import TimeTrackingPopup from "./TimeTrackingPopup";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * VIB-1208: én oppgave-rad på et internprosjekt i InternalProjectsSection
 * (desktop-tabell + mobil-kort). Egen fil for å holde
 * InternalProjectsSection under filstørrelsesgrensen.
 */
function useTaskRowState(task: InternalProjectTask, entries: TimeEntry[]) {
  const { activeTimer, startTimer, stopTimer, getElapsed } = useTimer();

  const loggedSeconds = entries
    .filter((e) => e.budget_line_id === task.id)
    .reduce((sum, e) => sum + (e.duration_seconds ?? 0), 0);
  const loggedHours = loggedSeconds / 3600;

  const isTaskActive = activeTimer?.taskId === task.id;
  const timerDisplay = isTaskActive ? formatSeconds(getElapsed()) : formatHours(loggedHours);

  const handleTimerToggle = () => {
    if (isTaskActive) {
      stopTimer();
    } else {
      startTimer({
        taskId: task.id,
        projectId: task.project_id,
        projectName: task.project_name,
        taskDescription: task.description,
        phase: "intern",
        budgetLineId: task.id,
      });
    }
  };

  return { isTaskActive, timerDisplay, handleTimerToggle };
}

export function InternalProjectTaskRow({
  task,
  userId,
  entries,
}: {
  task: InternalProjectTask;
  userId: string | null;
  entries: TimeEntry[];
}) {
  const [showTimePopup, setShowTimePopup] = useState(false);
  const timeAnchorRef = useRef<HTMLButtonElement>(null);
  const { isTaskActive, timerDisplay, handleTimerToggle } = useTaskRowState(task, entries);

  return (
    <tr className="border-b border-border/30 last:border-0 hover:bg-muted/20">
      <td className="py-2 px-4 pl-8">
        <span className="text-xs text-muted-foreground">{task.project_name}</span>
      </td>
      <td className="py-2 px-4">
        <span className="text-sm">{task.description}</span>
      </td>
      <td className="py-2 px-4 text-right tabular-nums text-sm text-muted-foreground">—</td>
      <td className="py-2 px-4">
        <div className="flex justify-center">
          <div className="flex items-center gap-2 w-[112px]">
            <button
              onClick={handleTimerToggle}
              aria-label={isTaskActive ? "Pause" : "Start"}
              className={cn(
                "w-8 h-8 shrink-0 flex items-center justify-center rounded-full border-[1.5px] transition-colors",
                isTaskActive
                  ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                  : "bg-card text-foreground border-foreground hover:bg-muted",
              )}
            >
              <SrIcon name={isTaskActive ? "pause" : "play"} className="w-3.5 h-3.5" />
            </button>
            <button
              ref={timeAnchorRef}
              onClick={() => setShowTimePopup(!showTimePopup)}
              className={cn(
                "font-mono text-sm cursor-pointer hover:underline text-left py-1 px-1 shrink-0 whitespace-nowrap",
                isTaskActive && "text-blue-600 font-semibold",
              )}
            >
              {timerDisplay}
            </button>
          </div>
          {showTimePopup && (
            <TimeTrackingPopup
              task={internalTaskToMyTask(task)}
              onClose={() => setShowTimePopup(false)}
              userId={userId}
              anchorRef={timeAnchorRef}
            />
          )}
        </div>
      </td>
      <td className="py-2 px-4 text-center">
        <span className="text-xs text-muted-foreground">—</span>
      </td>
    </tr>
  );
}

export function InternalProjectTaskMobileCard({
  task,
  userId,
  entries,
}: {
  task: InternalProjectTask;
  userId: string | null;
  entries: TimeEntry[];
}) {
  const [showTimePopup, setShowTimePopup] = useState(false);
  const timeAnchorRef = useRef<HTMLButtonElement>(null);

  const loggedSeconds = entries
    .filter((e) => e.budget_line_id === task.id)
    .reduce((sum, e) => sum + (e.duration_seconds ?? 0), 0);

  return (
    <div className="border-b border-border/30 last:border-0 pl-4">
      <button
        ref={timeAnchorRef}
        onClick={() => setShowTimePopup(!showTimePopup)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate">{task.description}</p>
          <p className="text-[11px] text-muted-foreground truncate">{task.project_name}</p>
        </div>
        <p className="font-mono text-sm tabular-nums flex-shrink-0">{formatHours(loggedSeconds / 3600)}</p>
      </button>
      {showTimePopup && (
        <TimeTrackingPopup
          task={internalTaskToMyTask(task)}
          onClose={() => setShowTimePopup(false)}
          userId={userId}
          anchorRef={timeAnchorRef}
        />
      )}
    </div>
  );
}
